import styled from '@emotion/styled';
import type { SxProps } from '@mui/material';
// Force TypeScript reload

/**
 * Modal Emotion Styled Components
 * Provides reusable styles for CRUD modals (Create, Edit, Delete)
 */

/**
 * Shared modal layout sx: rounded corners on the modal window and scrollable content.
 * Pass as modalSx to Modal (e.g. from BaseModal). window: overflow hidden so content
 * respects border-radius; windowBody: maxHeight + overflowY auto so content scrolls
 * (viewport-based max so it works without changing the modal manager layout).
 */
export const sharedModalLayoutSx: {
  window?: Record<string, unknown>;
  windowBody?: SxProps;
  dialogRoot?: SxProps;
} = {
  window: {
    overflow: 'hidden',
    borderRadius: 12,
    height: '100%',
    maxHeight: '100%',
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    /* ModalContainer (direct child) needs minHeight: 0 so flex chain
       properly constrains DialogContent and allows scrolling */
    '& > div': {
      minHeight: 0,
    },
  },
  windowBody: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    padding: 0,
    paddingTop: 0,
  },
  dialogRoot: {
    '& .MuiDialog-container': {
      padding: '36px 240px',
    },
    '& .MuiPaper-root': {
      maxWidth: 'none',
    },
  },
};

/* ============================================================================
 * Shared Modal Layout Components
 * Reusable across all modals (BulkEdit, Confirm, Create, Edit, etc.)
 * ============================================================================ */

/** Shared: modal header — renders title, optional description, and close button.
 *  Gap between title and description auto-adjusts when description is absent. */
export const ModalHeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.s.xxs};
  padding: ${p => p.theme.s.md} ${p => p.theme.s.lg};
  position: relative;
  border-bottom: 0.5px solid ${p => p.theme.palette.border.primary};
`;

/** Shared: title row inside header — icon + title text */
export const ModalHeaderTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.s.xs};
  padding-right: ${p => p.theme.s.xxl};
`;

/** Shared: close button container — absolute top-right inside ModalHeaderSection */
export const ModalHeaderClose = styled.div`
  position: absolute;
  top: ${p => p.theme.s.md};
  right: ${p => p.theme.s.md};
  display: flex;
  align-items: center;
`;

/** Shared: wrapper for body + footer so footer stays at bottom of modal */
export const ModalBodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow-x: hidden;
`;

/** Shared: scrollable body content area — sits above the footer */
export const ModalScrollBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: ${p => p.theme.s.md} ${p => p.theme.s.lg} ${p => p.theme.s.lg};
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.s.sm};
`;

/** Shared: fixed footer with border-top — sits at the bottom of modal */
export const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  overflow: clip;
  padding: ${p => p.theme.s.md} ${p => p.theme.s.lg};
  border-top: 0.5px solid ${p => p.theme.palette.border.primary};
`;

/** Shared: action buttons row inside footer */
export const ModalFooterActions = styled.div`
  display: flex;
  gap: ${p => p.theme.s.sm};
`;

/* Form container - used in Create and Edit modals */
export const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.s.md};
  margin-top: ${p => p.theme.s.xs};
`;

/* Content container - general purpose modal content wrapper */
export const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: ${p => p.theme.s.xs};
`;

/* Field group - label + input/display grouping */
export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.s.xs};
`;

/* Warning box - used in Delete confirmation modals */
export const WarningBox = styled.div`
  padding: ${p => p.theme.s.md};
  border-radius: ${p => p.theme.s.xs};
  background-color: ${p => p.theme.palette.bg.warningSubtle};
  border: 1px solid ${p => p.theme.palette.border.warning};
`;

/* Warning row - icon + text layout */
export const WarningRow = styled.div`
  display: flex;
  gap: ${p => p.theme.s.sm};
`;

/* Warning text group - title + description */
export const WarningTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.s.xxs};
`;

/* Warning icon wrapper - centered icon container */
export const WarningIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${p => p.theme.s.xs};
  background-color: ${p => p.theme.palette.bg.warningSubtle};
  flex-shrink: 0;
`;

/* Bulk edit: non-editable items warning block */
export const NonEditableWarningBlock = styled.div`
  padding: ${p => p.theme.s.md};
  border-radius: ${p => p.theme.s.xs};
  background-color: ${p => p.theme.palette.bg.warningSubtle};
  border: 1px solid ${p => p.theme.palette.border.warning};
  margin-bottom: ${p => p.theme.s.xs};
`;

/* Bulk edit: warning heading (icon + message); message wraps with flex: 1 */
export const NonEditableWarningHeading = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${p => p.theme.s.xs};
  flex: 1;
  min-width: 0;
  font-size: ${p => p.theme.typography?.bodySm?.fontSize ?? '0.875rem'};
  line-height: ${p => p.theme.typography?.bodySm?.lineHeight ?? '1.25rem'};

  & svg {
    flex-shrink: 0;
  }
  & > span {
    flex: 1;
    min-width: 0;
    font-weight: 500;
    color: ${p => p.theme.palette.text.primary};
  }
`;

/* Bulk edit: full heading row (icon + message only) */
export const RemovedWarningHeadingRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${p => p.theme.s.sm};
  width: 100%;
`;

/* Bulk edit: divider row - label | line | chevron (same color as warning icon) */
export const RemovedWarningDividerRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.s.sm};
  width: 100%;
  margin-top: ${p => p.theme.s.xs};
`;

export const RemovedWarningDividerLabel = styled.span`
  font-size: ${p => p.theme.typography?.bodyXs?.fontSize ?? '0.75rem'};
  font-weight: 500;
  line-height: ${p => p.theme.typography?.bodyXs?.lineHeight ?? '1.125rem'};
  color: ${p =>
    (p.theme.palette.fg as { warningPrimary?: string })?.warningPrimary ??
    p.theme.palette.border?.primary};
  flex-shrink: 0;
`;

export const RemovedWarningDividerLine = styled.div`
  flex: 1;
  min-width: 0;
  border-top: 1px solid
    ${p =>
      (p.theme.palette.fg as { warningPrimary?: string })?.warningPrimary ??
      p.theme.palette.border.primary};
`;

export const RemovedWarningChevronButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${p => p.theme.palette.fg.warningPrimary};
  display: flex;
  align-items: center;
  flex-shrink: 0;
  & svg,
  & svg path {
    color: inherit;
    fill: currentColor;
  }
`;

/* Bulk edit: thin separator below warning heading (same color as warning icon) */
export const NonEditableWarningSeparator = styled.div`
  border-top: 1px solid
    ${p =>
      (p.theme.palette.fg as { warningPrimary?: string })?.warningPrimary ??
      p.theme.palette.border.primary};
  margin: ${p => p.theme.s.xs} 0 0 0;
`;

/* Bulk edit: list area for removed items (Name · ID lines); padding only, no inner box */
export const NonEditableListBox = styled.div`
  padding: ${p => p.theme.s.xxxs} 0 ${p => p.theme.s.xxxs} 0;
  margin-top: ${p => p.theme.s.xxxs};

  & > .MuiList-root .MuiListItem-root {
    padding: ${p => p.theme.s.xxxs} 0;
  }
`;

/* Bulk edit: single line in removed list - Name (primary) · ID (secondary) */
export const RemovedItemLine = styled.div`
  font-size: ${p => p.theme.typography?.bodyXs?.fontSize ?? '0.75rem'};
  line-height: ${p => p.theme.typography?.bodyXs?.lineHeight ?? '1.125rem'};

  & .removed-item-name {
    color: ${p => p.theme.palette.text.primary};
    font-weight: 500;
  }
  & .removed-item-id {
    color: ${p => p.theme.palette.text.secondary};
  }
`;

/* Bulk edit: "Show all" / "Show less" toggle button for long affected items list */
export const ShowAllButton = styled.button`
  background: none;
  border: none;
  padding: ${p => p.theme.s.sm} 0 ${p => p.theme.s.xxxs} 0;
  cursor: pointer;
  color: ${p => p.theme.palette.text.secondary};
  font-size: ${p => p.theme.typography?.bodyXs?.fontSize ?? '0.75rem'};
  line-height: ${p => p.theme.typography?.bodyXs?.lineHeight ?? '1.125rem'};
  text-align: left;
  display: block;

  &:hover {
    text-decoration: underline;
  }
`;

/* Display box - for showing readonly data (like API keys) */
export const DisplayBox = styled.div`
  padding: ${p => p.theme.s.md};
  border-radius: 6px;
  background-color: ${p => p.theme.palette.bg.tertiary};
  border: 1px solid ${p => p.theme.palette.border.primary};
  font-family: monospace;
  font-size: 14px;
  word-break: break-all;
`;

/* Key name display box - for showing item names */
export const KeyNameDisplayBox = styled.div`
  padding: ${p => p.theme.s.sm} ${p => p.theme.s.md};
  border-radius: 6px;
  background-color: ${p => p.theme.palette.bg.tertiary};
  border: 1px solid ${p => p.theme.palette.border.primary};
`;

/* Primary variant for key name display (lighter background) */
export const KeyNameDisplayBoxPrimary = styled.div`
  padding: ${p => p.theme.s.sm} ${p => p.theme.s.md};
  border-radius: 6px;
  border: 1px solid ${p => p.theme.palette.border.primary};
`;

/* Secret display box - for showing sensitive values like API keys */
export const SecretDisplayBox = styled.div`
  padding: ${p => p.theme.s.md};
  border-radius: 6px;
  border: 1px solid ${p => p.theme.palette.border.primary};
  font-family: monospace;
  font-size: 14px;
  word-break: break-all;
`;

/* API key display box - alias for backward compatibility */
export const ApiKeyDisplayBox = styled(SecretDisplayBox)``;

/* Success container - used after successful creation */
export const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.s.md};
  margin-top: ${p => p.theme.s.xs};
`;

/* Action button row - for modal action buttons */
export const ActionRow = styled.div`
  display: flex;
  gap: ${p => p.theme.s.sm};
  justify-content: flex-end;
  margin-top: ${p => p.theme.s.md};
`;

/* Full width button variant */
export const FullWidthButton = styled.div`
  width: 100%;
`;

/* Modal description text */
export const Description = styled.div`
  margin-bottom: ${p => p.theme.s.xs};
`;

/* Input wrapper with label */
export const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.s.xxs};
`;

/* Label text */
export const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
`;

/* Helper text below inputs */
export const HelperText = styled.span`
  font-size: 12px;
  margin-top: ${p => p.theme.s.xxs};
`;

/* Error state text */
export const ErrorText = styled.span`
  color: ${p => p.theme.palette.text.error};
`;

/* Success state text */
export const SuccessText = styled.span`
  color: ${p => p.theme.palette.text.success};
`;

/* Required field label container */
export const LabelContainer = styled.span`
  display: inline-flex;
  align-items: center;
`;

/* Required field red asterisk indicator */
export const RequiredAsterisk = styled.span`
  color: ${p => p.theme.palette.text.error};
  margin-left: ${p => p.theme.s.xxs};
`;

/* Number arrows container - vertical layout for increment/decrement buttons */
export const NumberArrowsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

/* Number arrow button - compact styling for increment/decrement */
export const NumberArrowButton = styled.button`
  padding: 2px;
  min-width: unset;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.palette.text.secondary};

  &:hover:not(:disabled) {
    color: ${p => p.theme.palette.text.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/* SX props for number arrows (for use with MUI components) */
export const numberArrowsStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  button: {
    padding: '2px',
    minWidth: 'unset',
  },
};

/* Auto-generate button row - clickable row with icon and label */
export const AutoGenerateRow = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.s.xxs};
  margin-top: ${p => p.theme.s.xxs};
  cursor: ${p => (p.disabled ? 'default' : 'pointer')};
  user-select: none;

  &:hover {
    opacity: ${p => (p.disabled ? 1 : 0.8)};
  }
`;

/* Form section header - used to separate groups of fields */
export const FormSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.s.sm};
  margin-top: ${p => p.theme.s.md};
  padding-bottom: ${p => p.theme.s.xxs};
  border-bottom: 1px solid ${p => p.theme.palette.border.primary};
`;

/* Form section container - wraps a group of related fields */
export const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.s.xxs};
`;

/* Bulk edit: form container with tighter spacing (BulkEdit-specific, does not affect Create/Edit modals) */
export const BulkEditFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.s.sm};
  min-width: 0;
`;

/* Bulk edit: wrapper for search bar; same bodyXs input/placeholder as form fields */
export const BulkEditSearchWrapper = styled.div`
  flex-shrink: 0;
  width: 100%;
  min-width: 0;
  padding: ${p => p.theme.s.md} ${p => p.theme.s.lg} 0;
  background: ${p => p.theme.palette.bg.primary};
  overflow: hidden;
  max-height: 120px;
  opacity: 1;
  will-change: max-height, opacity;
  transition:
    max-height 0.2s ease,
    opacity 0.15s ease,
    padding 0.2s ease;

  &[data-collapsed='true'] {
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
    opacity: 0;
    pointer-events: none;
  }
`;

/* Bulk edit: form section as single grid so column 1 = widest cell, arrows share one track */
export const BulkEditFormSection = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) fit-content(100%) minmax(0, 2fr);
  grid-auto-rows: auto;
  column-gap: ${p => p.theme.s.lg};
  row-gap: ${p => p.theme.s.sm};
  align-items: start;
  min-width: 0;
  padding-bottom: ${p => p.theme.s.lg};
`;

/* Bulk edit: section header with tighter margin */
export const BulkEditFormSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.s.sm};
  margin-top: ${p => p.theme.s.sm};
  padding-top: ${p => p.theme.s.xs};
  border-top: 1px solid ${p => p.theme.palette.border.primary};

  &:first-of-type {
    margin-top: 0;
  }
`;

/* Bulk edit: row wrapper - display: contents so its 3 children participate in section grid */
export const BulkEditRow = styled.div`
  display: contents;
`;

/* Bulk edit: grid column header row ("Preview" | "New changes") - sits inside the grid as the first row */
export const BulkEditGridHeader = styled.div`
  display: contents;
`;

/* Bulk edit: pill for "Mixed values" - compact, width fits text only */
export const MixedValuesPill = styled.span`
  display: inline-block;
  width: fit-content;
  border-radius: 6px;
  padding: 1px 4px;
  background-color: ${p => p.theme.palette.bg.tertiary};
  font-size: 12px;
  color: ${p => p.theme.palette.text.secondary};
  white-space: nowrap;
`;

/* Bulk edit: wrapper that hides the label of the form field (input only in right column); min-width 0 so grid can shrink */
export const BulkEditInputOnly = styled.div<{ $multiline?: boolean }>`
  display: flex;
  align-items: ${p => (p.$multiline ? 'stretch' : 'center')};
  min-height: ${p => (p.$multiline ? 'auto' : '44px')};
  min-width: 0;
  padding-right: 1px; /* Prevent border clipping from overflow-x: hidden */
  & label,
  & .MuiInputLabel-root {
    display: none !important;
  }
  & > * {
    width: 100%;
    min-width: 0;
  }
  && .MuiInputBase-root.MuiInputBase-adornedStart {
    padding-left: 14px;
  }
  /* Cap input height at 44px for non-multiline so grid row-gap stays accurate */
  ${p =>
    !p.$multiline &&
    `
    & .MuiInputBase-root {
      height: 44px;
    }
  `}
`;

/* Bulk edit: expandable text container for current values */
export const ExpandableTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

/* Bulk edit: expandable text with line clamp support */
export const ExpandableText = styled.span<{ $expanded: boolean }>`
  display: ${p => (p.$expanded ? 'block' : '-webkit-box')};
  -webkit-line-clamp: ${p => (p.$expanded ? 'unset' : 2)};
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;

/* Bulk edit: show more/less link button */
export const ShowMoreLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 11px;
  color: ${p => p.theme.palette.text.brand};
  align-self: flex-start;

  &:hover {
    text-decoration: underline;
  }
`;

/** Bulk edit: label column with field name and current value */
export const BulkEditLabelColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
`;

/* Bulk edit: multi-line preview for description fields (2-line clamp) */
export const BulkEditMultiLinePreview = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  max-width: 100%;
`;

/* Bulk edit: avatar + text preview for dropdown fields */
export const BulkEditAvatarPreview = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.s.xxs};
  min-width: 0;
`;

/** Bulk edit: arrow separator between current value and input */
export const BulkEditArrowSeparator = styled.div`
  min-width: 24px;
  flex-shrink: 0;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.palette.border.primary};
`;

/** Bulk edit: wrapper around the form field input */
export const BulkEditFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

/* ============================================================================
 * Bulk Edit Confirmation Modal Styles
 * ============================================================================ */

/* Confirm modal: main container with column layout (body + footer) */
export const ConfirmModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

/* Confirm modal: body wrapper containing sidebar and content side-by-side */
export const ConfirmModalBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

/* Confirm modal: header row spanning full width */
export const ConfirmModalHeaderRow = styled.div`
  display: flex;
  flex-shrink: 0;
`;

/* Confirm modal: left sidebar (impacted items list) */
export const ConfirmModalSidebar = styled.div<{ $collapsed: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${p => (p.$collapsed ? '48px' : '360px')};
  min-width: ${p => (p.$collapsed ? '48px' : '360px')};
  border-right: 0.5px solid ${p => p.theme.palette.border.primary};
  transition:
    width 0.2s ease,
    min-width 0.2s ease;
  overflow: hidden;
  min-height: 0;
`;

/* Confirm modal: sidebar header with back button and count (used inside ConfirmModalHeaderRow) */
export const ConfirmSidebarHeader = styled.div<{ $collapsed: boolean }>`
  display: flex;
  flex-direction: ${p => (p.$collapsed ? 'column' : 'row')};
  align-items: center;
  justify-content: ${p => (p.$collapsed ? 'center' : 'flex-start')};
  gap: ${p => (p.$collapsed ? p.theme.s.xxs : p.theme.s.xs)};
  padding: ${p => p.theme.s.lg} ${p => p.theme.s.md} 0;
  width: ${p => (p.$collapsed ? '48px' : '360px')};
  min-width: ${p => (p.$collapsed ? '48px' : '360px')};
  border-right: 0.5px solid ${p => p.theme.palette.border.primary};
  flex-shrink: 0;
  cursor: pointer;
  transition:
    width 0.2s ease,
    min-width 0.2s ease;

  &:hover {
    background-color: ${p => p.theme.palette.bg.secondary};
  }

  & svg {
    color: ${p => p.theme.palette.border.primary};
  }
`;

/* Confirm modal: item count badge */
export const ConfirmItemCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${p => p.theme.s.xxxs} ${p => p.theme.s.xs};
  border-radius: 6px;
  background-color: ${p => p.theme.palette.bg.tertiary};
  font-size: ${p => p.theme.typography?.bodyXs?.fontSize ?? '0.75rem'};
  font-weight: 600;
  color: ${p => p.theme.palette.text.quaternary};
`;

/* Confirm modal: scrollable items list */
export const ConfirmItemsList = styled.div<{ $hidden?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.s.sm};
  flex: 1;
  overflow-y: auto;
  padding: ${p => p.theme.s.md} ${p => p.theme.s.lg} ${p => p.theme.s.lg};
  visibility: ${p => (p.$hidden ? 'hidden' : 'visible')};
`;

/* Confirm modal: single item row */
export const ConfirmItemRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${p => p.theme.s.xxs};
  font-size: ${p => p.theme.typography?.bodySm?.fontSize ?? '0.875rem'};
  line-height: ${p => p.theme.typography?.bodySm?.lineHeight ?? '1.25rem'};

  & .confirm-item-number {
    color: ${p => p.theme.palette.text.placeholder};
    font-weight: 400;
    flex-shrink: 0;
  }

  & .confirm-item-name {
    color: ${p => p.theme.palette.text.secondary};
    font-weight: 600;
    min-width: 0;
  }

  & .confirm-item-dot {
    color: ${p => p.theme.palette.text.secondary};
    flex-shrink: 0;
  }

  & .confirm-item-id {
    color: ${p => p.theme.palette.text.placeholder};
    min-width: 0;
  }
`;

/* Confirm modal: right content area (changes list) */
export const ConfirmModalContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

/* Confirm modal: changes section header (used inside ConfirmModalHeaderRow) */
export const ConfirmChangesHeader = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  padding: ${p => p.theme.s.lg} ${p => p.theme.s.lg} 0;
  flex-shrink: 0;
`;

/* Confirm modal: scrollable changes list */
export const ConfirmChangesList = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) fit-content(100%) minmax(0, 3fr);
  grid-auto-rows: auto;
  column-gap: ${p => p.theme.s.lg};
  row-gap: ${p => p.theme.s.md};
  flex: 1;
  overflow-y: auto;
  padding: ${p => p.theme.s.md} ${p => p.theme.s.lg} ${p => p.theme.s.lg};
  align-items: center;
  align-content: start;
`;

/* Confirm modal: single change row with field name -> new value */
export const ConfirmChangeRow = styled.div`
  display: contents;
  font-size: ${p => p.theme.typography?.bodySm?.fontSize ?? '0.875rem'};
  line-height: ${p => p.theme.typography?.bodySm?.lineHeight ?? '1.25rem'};

  & .confirm-change-field {
    color: ${p => p.theme.palette.text.secondary};
    font-size: ${p => p.theme.typography?.bodySm?.fontSize ?? '0.875rem'};
    line-height: ${p => p.theme.typography?.bodySm?.lineHeight ?? '1.25rem'};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & .confirm-change-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  & .confirm-change-value {
    color: ${p => p.theme.palette.text.secondary};
    font-size: ${p => p.theme.typography?.bodySm?.fontSize ?? '0.875rem'};
    line-height: ${p => p.theme.typography?.bodySm?.lineHeight ?? '1.25rem'};
    font-weight: 400;
    display: flex;
    align-items: center;
    gap: ${p => p.theme.s.xs};
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/* Confirm modal: brand/entity indicator dot */
export const ConfirmValueDot = styled.span<{ $color?: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${p => p.$color ?? p.theme.palette.fg.brand};
  flex-shrink: 0;
`;
